<template>
  <div class="user-api-usage-report">
    <!-- 头部说明与时间筛选 -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div>
        <h5 class="font-weight-bold mb-1 d-flex align-items-center">
          <b-icon icon="graph-up" class="mr-2 text-primary"></b-icon>
          API 用量报表
        </h5>
        <p class="text-muted small mb-0">实时统计您的 zzshare SDK 数据接口调用频次与分布</p>
      </div>

      <div class="d-flex align-items-center mt-2 mt-sm-0">
        <!-- 周期切换 -->
        <b-button-group size="sm" class="mr-2">
          <b-button
            :variant="rangeDays === 1 ? 'primary' : 'outline-secondary'"
            @click="switchRange(1)"
          >
            今日 (24h)
          </b-button>
          <b-button
            :variant="rangeDays === 7 ? 'primary' : 'outline-secondary'"
            @click="switchRange(7)"
          >
            近 7 天
          </b-button>
          <b-button
            :variant="rangeDays === 30 ? 'primary' : 'outline-secondary'"
            @click="switchRange(30)"
          >
            近 30 天
          </b-button>
        </b-button-group>

        <!-- 刷新 -->
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="fetchUsage"
          :disabled="loading"
          title="刷新数据"
        >
          <b-icon icon="arrow-clockwise" :animation="loading ? 'spin' : ''"></b-icon>
        </b-button>
      </div>
    </div>

    <!-- 4 个统计卡片 -->
    <div class="row mb-3">
      <div class="col-md-3 col-6 mb-2 mb-md-0">
        <div class="p-3 border rounded shadow-sm usage-card bg-card text-center">
          <div class="text-muted small mb-1">今日调用总数</div>
          <div class="h3 font-weight-bold text-primary mb-0">{{ formatNumber(todayTotal) }}</div>
          <div class="text-muted x-small mt-1">次请求</div>
        </div>
      </div>

      <div class="col-md-3 col-6 mb-2 mb-md-0">
        <div class="p-3 border rounded shadow-sm usage-card bg-card text-center">
          <div class="text-muted small mb-1">周期累计请求</div>
          <div class="h3 font-weight-bold text-success mb-0">{{ formatNumber(periodTotal) }}</div>
          <div class="text-muted x-small mt-1">{{ rangeDays === 1 ? '今日全天' : `近 ${rangeDays} 天` }}</div>
        </div>
      </div>

      <div class="col-md-3 col-6 mb-2 mb-md-0">
        <div class="p-3 border rounded shadow-sm usage-card bg-card text-center">
          <div class="text-muted small mb-1">调用接口种类</div>
          <div class="h3 font-weight-bold text-info mb-0">{{ Object.keys(activeEndpointsMap).length }}</div>
          <div class="text-muted x-small mt-1">个已使用端点</div>
        </div>
      </div>

      <div class="col-md-3 col-6 mb-2 mb-md-0">
        <div class="p-3 border rounded shadow-sm usage-card bg-card text-center">
          <div class="text-muted small mb-1">最频繁接口</div>
          <div class="h5 font-weight-bold text-warning text-truncate mb-0" :title="topEndpointName">
            {{ topEndpointName }}
          </div>
          <div class="text-muted x-small mt-1">{{ topEndpointCount }} 次调用</div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <div class="text-muted small mt-2">正在加载用量报表...</div>
    </div>

    <!-- 无数据状态 -->
    <div v-else-if="periodTotal === 0" class="text-center py-5 border rounded bg-card mb-3">
      <b-icon icon="inbox" font-scale="3" class="text-muted mb-2"></b-icon>
      <p class="text-muted mb-2">当前周期内暂无 API 调用记录</p>
      <div class="small text-muted">
        您可以使用 Python 运行 <a href="https://github.com/zzquant/zzshare" target="_blank" class="font-weight-bold text-primary">zzshare SDK</a> 进行数据获取，调用将在此处实时呈现。
      </div>
    </div>

    <!-- 有数据时的图表与明细 -->
    <template v-else>
      <div class="row mb-3">
        <!-- 走势柱状/折线图 -->
        <div class="col-lg-7 col-12 mb-3 mb-lg-0">
          <div class="p-3 border rounded shadow-sm bg-card h-100">
            <h6 class="font-weight-bold mb-2 border-left border-primary pl-2">
              {{ rangeDays === 1 ? '今日 24 小时每小时请求走势' : `近 ${rangeDays} 天每日请求走势` }}
            </h6>
            <div style="height: 270px;">
              <v-chart :options="trendChartOption" :autoresize="true" style="width: 100%; height: 100%;"></v-chart>
            </div>
          </div>
        </div>

        <!-- 接口分布环形图 -->
        <div class="col-lg-5 col-12">
          <div class="p-3 border rounded shadow-sm bg-card h-100">
            <h6 class="font-weight-bold mb-2 border-left border-info pl-2">
              各接口调用占比
            </h6>
            <div style="height: 270px;">
              <v-chart :options="endpointPieOption" :autoresize="true" style="width: 100%; height: 100%;"></v-chart>
            </div>
          </div>
        </div>
      </div>

      <!-- 接口调用明细表格 -->
      <div class="p-3 border rounded shadow-sm bg-card">
        <h6 class="font-weight-bold mb-3 border-left border-success pl-2">
          接口调用明细列表
        </h6>
        <div class="table-responsive">
          <table class="table table-hover mb-0 custom-usage-table">
            <thead class="bg-light">
              <tr>
                <th>接口名称 (zzshare)</th>
                <th>功能说明</th>
                <th class="text-right">累计请求</th>
                <th class="text-right" style="width: 180px;">占比</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cnt, ep) in activeEndpointsMap" :key="ep">
                <td>
                  <code class="text-primary font-weight-bold h6">{{ ep }}()</code>
                </td>
                <td class="text-muted small">
                  {{ formatEndpointDesc(ep) }}
                </td>
                <td class="text-right font-weight-bold text-success">
                  {{ formatNumber(cnt) }}
                </td>
                <td class="text-right align-middle">
                  <div class="d-flex align-items-center justify-content-end">
                    <div class="progress flex-grow-1 mr-2" style="height: 6px; max-width: 90px;">
                      <div
                        class="progress-bar bg-primary"
                        :style="{ width: calcPercent(cnt, periodTotal) + '%' }"
                      ></div>
                    </div>
                    <span class="x-small text-muted" style="min-width: 45px;">
                      {{ calcPercent(cnt, periodTotal) }}%
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request.js'

export default {
  name: 'UserApiUsageReport',
  data() {
    return {
      rangeDays: 1, // 1 (今日), 7, 30
      loading: false,
      todayTotal: 0,
      periodTotal: 0,
      singleDayData: null,
      seriesData: null,
      friendlyEndpoints: {
        stk_mins: { name: 'stk_mins', desc: '个股分钟K线行情数据' },
        daily: { name: 'daily', desc: '个股日线复权K线行情数据' },
        open_stock_info: { name: 'open_stock_info', desc: '股票所属板块与基础画像' },
        lhb: { name: 'lhb', desc: '交易所龙虎榜每日席位明细' },
        sentiment: { name: 'sentiment', desc: '全市场短线情绪与周期指标' },
        plate_kline: { name: 'plate_kline', desc: '题材与行业板块日/分时K线' },
        uplimit: { name: 'uplimit', desc: '每日涨停封板与连板晋级池' },
        realtime: { name: 'realtime', desc: '全市场实时行情盘口快照' },
      }
    }
  },
  computed: {
    activeEndpointsMap() {
      if (this.rangeDays === 1) {
        return (this.singleDayData && this.singleDayData.endpoints) || {}
      }
      // 多天聚合各端点
      const agg = {}
      if (this.seriesData && this.seriesData.daily_series) {
        this.seriesData.daily_series.forEach(d => {
          const eps = d.endpoints || {}
          Object.keys(eps).forEach(k => {
            agg[k] = (agg[k] || 0) + eps[k]
          })
        })
      }
      return dictSorted(agg)
    },
    topEndpointName() {
      const keys = Object.keys(this.activeEndpointsMap)
      return keys.length > 0 ? keys[0] : '-'
    },
    topEndpointCount() {
      const vals = Object.values(this.activeEndpointsMap)
      return vals.length > 0 ? vals[0] : 0
    },
    trendChartOption() {
      let xData = []
      let yData = []

      if (this.rangeDays === 1) {
        // 今日 24 小时
        const hourly = (this.singleDayData && this.singleDayData.hourly) || {}
        for (let i = 0; i < 24; i++) {
          const hStr = (i < 10 ? '0' : '') + i
          xData.push(`${hStr}:00`)
          yData.push(hourly[hStr] || 0)
        }
      } else {
        // 近 7 天或 30 天每日趋势
        const list = (this.seriesData && this.seriesData.daily_series) || []
        list.forEach(item => {
          const rawD = item.date || ''
          const label = rawD.length >= 8 ? `${rawD.substr(4, 2)}-${rawD.substr(6, 2)}` : rawD
          xData.push(label)
          yData.push(item.total || 0)
        })
      }

      const isSingleDay = this.rangeDays === 1

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: isSingleDay ? 'shadow' : 'line' }
        },
        grid: {
          top: '12%',
          left: '3%',
          right: '4%',
          bottom: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xData,
          axisLine: { lineStyle: { color: '#cbd5e1' } },
          axisLabel: { color: '#64748b', interval: isSingleDay ? 2 : 'auto' }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: { lineStyle: { color: '#cbd5e1' } },
          splitLine: { lineStyle: { color: '#e2e8f0', type: 'dashed' } },
          axisLabel: { color: '#64748b' }
        },
        series: [
          {
            name: '请求次数',
            type: isSingleDay ? 'bar' : 'line',
            smooth: true,
            data: yData,
            itemStyle: { color: '#3b82f6' },
            areaStyle: isSingleDay ? null : {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(59, 130, 246, 0.4)' },
                  { offset: 1, color: 'rgba(59, 130, 246, 0.05)' }
                ]
              }
            }
          }
        ]
      }
    },
    endpointPieOption() {
      const map = this.activeEndpointsMap
      const data = Object.keys(map).map(k => ({
        name: k,
        value: map[k]
      }))
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} 次 ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: '2%',
          top: 'center',
          textStyle: { color: '#64748b' }
        },
        series: [
          {
            name: '接口占比',
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['40%', '50%'],
            avoidLabelOverlap: false,
            label: { show: false },
            emphasis: {
              label: {
                show: true,
                fontSize: '13',
                fontWeight: 'bold'
              }
            },
            data: data
          }
        ]
      }
    }
  },
  mounted() {
    this.fetchUsage()
  },
  methods: {
    switchRange(days) {
      if (this.rangeDays === days) return
      this.rangeDays = days
      this.fetchUsage()
    },
    async fetchUsage() {
      this.loading = true
      try {
        const res = await request_zzquant({
          url: '/v3/user/api-usage',
          method: 'GET',
          params: { days: this.rangeDays }
        })
        if (res.data && (res.data.code === 20000 || res.data.code === 200)) {
          const d = res.data.data
          if (this.rangeDays === 1) {
            this.singleDayData = d
            this.periodTotal = d.total || 0
            this.todayTotal = d.total || 0
          } else {
            this.seriesData = d
            this.periodTotal = d.grand_total || 0
            // 取今日用量
            const todayItem = (d.daily_series && d.daily_series[d.daily_series.length - 1]) || {}
            this.todayTotal = todayItem.total || 0
          }
        }
      } catch (err) {
        console.error('fetchUsage error:', err)
      } finally {
        this.loading = false
      }
    },
    formatNumber(num) {
      if (!num) return '0'
      return num.toLocaleString()
    },
    calcPercent(part, total) {
      if (!total || !part) return '0.0'
      return ((part / total) * 100).toFixed(1)
    },
    formatEndpointDesc(ep) {
      const item = this.friendlyEndpoints[ep]
      return item ? item.desc : '金融量化业务数据接口'
    }
  }
}

function dictSorted(obj) {
  return Object.fromEntries(Object.entries(obj).sort(([, a], [, b]) => b - a))
}
</script>

<style scoped>
.user-api-usage-report {
  color: var(--text-main, #333);
}

.bg-card {
  background-color: var(--bg-card, #ffffff);
}

.usage-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.usage-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.x-small {
  font-size: 0.75rem;
}

.custom-usage-table th {
  border-top: none;
  font-size: 0.85rem;
  color: var(--text-muted, #64748b);
}

.custom-usage-table td {
  vertical-align: middle;
}
</style>
